import React, { useState, useEffect } from 'react';
import Tilt from 'react-parallax-tilt';
import { motion, AnimatePresence } from 'framer-motion';
import { Star, TrendingUp, TrendingDown, Zap, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { getCardImageById } from '../utils/cardImageHelper';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rarityColors = {
  common: { main: '#A0A0A0', glow: 'rgba(160, 160, 160, 0.4)' },
  rare: { main: '#00CCFF', glow: 'rgba(0, 204, 255, 0.5)' },
  epic: { main: '#FF00FF', glow: 'rgba(255, 0, 255, 0.6)' },
  legendary: { main: '#FFD700', glow: 'rgba(255, 215, 0, 0.7)' },
};

const EnhancedCard = ({ card, onClick, showNews = true }) => {
  const [cardStats, setCardStats] = useState(null);
  const [currentEvent, setCurrentEvent] = useState(null);
  
  const colors = rarityColors[card.rarity] || rarityColors.common;
  
  useEffect(() => {
    if (showNews && card.id) {
      fetchCardStats();
      const interval = setInterval(fetchCardStats, 30000);
      return () => clearInterval(interval);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [card.id, showNews]);
  
  const fetchCardStats = async () => {
    try {
      const response = await axios.get(`${API}/news/card-stats/${card.id}`);
      setCardStats(response.data);
      
      if (response.data.active_events?.length > 0) {
        setCurrentEvent(response.data.active_events[response.data.active_events.length - 1]);
      }
    } catch (error) {
      // Stats not available, use base card data
    }
  };
  
  const currentPower = cardStats?.current_power || card.power_rating;
  const powerModifier = cardStats?.power_modifier || 0;
  const isBuffed = powerModifier > 0;
  const isDebuffed = powerModifier < 0;
  
  return (
    <Tilt
      tiltMaxAngleX={12}
      tiltMaxAngleY={12}
      glareEnable={true}
      glareMaxOpacity={0.4}
      glareColor={colors.main}
      glarePosition="all"
      glareBorderRadius="16px"
      scale={1.03}
    >
      <motion.div
        data-testid={`enhanced-card-${card.id}`}
        className="relative rounded-2xl overflow-hidden aspect-[3/4] cursor-pointer group"
        onClick={onClick}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        style={{
          boxShadow: `0 0 30px ${colors.glow}, inset 0 0 20px rgba(0,0,0,0.5)`,
          border: `3px solid ${colors.main}`,
        }}
      >
        {/* Background with cyberpunk grid */}
        <div className="absolute inset-0 bg-[#0a0a0a]">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `
                linear-gradient(${colors.main}20 1px, transparent 1px),
                linear-gradient(90deg, ${colors.main}20 1px, transparent 1px)
              `,
              backgroundSize: '20px 20px',
            }}
          />
        </div>
        
        {/* Card Image */}
        <div className="relative z-10 p-3 h-full flex flex-col">
          <div className="flex-1 relative overflow-hidden rounded-xl">
            <img
              src={getCardImageById(card)}
              alt={card.character_name}
              className="w-full h-full object-contain"
              data-testid={`card-image-${card.id}`}
              onError={(e) => {
                // Fallback to generated placeholder
                e.target.src = getCardImageById({ ...card, image_base64: null, image: null });
              }}
            />
            
            {/* Scanlines effect */}
            <div 
              className="absolute inset-0 pointer-events-none opacity-10"
              style={{
                backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.3) 2px, rgba(0,0,0,0.3) 4px)',
              }}
            />
          </div>
        </div>
        
        {/* Holographic shine */}
        <motion.div
          className="absolute inset-0 pointer-events-none z-20"
          style={{
            background: `linear-gradient(105deg, transparent 40%, ${colors.main}30 50%, transparent 60%)`,
          }}
          animate={{ x: [-300, 300] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'linear', repeatDelay: 2 }}
        />
        
        {/* Power Rating with modifier */}
        <div className="absolute top-3 right-3 z-30">
          <motion.div
            className="flex items-center gap-1 px-2 py-1 rounded-lg"
            style={{ 
              background: isBuffed ? 'rgba(0,255,0,0.3)' : isDebuffed ? 'rgba(255,0,0,0.3)' : 'rgba(0,0,0,0.7)',
              border: `2px solid ${isBuffed ? '#00FF00' : isDebuffed ? '#FF0000' : colors.main}`,
            }}
            animate={powerModifier !== 0 ? { scale: [1, 1.1, 1] } : {}}
            transition={{ duration: 0.5, repeat: Infinity }}
          >
            {isBuffed && <TrendingUp className="w-4 h-4 text-green-400" />}
            {isDebuffed && <TrendingDown className="w-4 h-4 text-red-400" />}
            <div className="flex gap-0.5">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  className={`w-3 h-3 ${
                    i < currentPower ? 'fill-yellow-400 text-yellow-400' : 'text-gray-600'
                  }`}
                />
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Rarity indicator */}
        <div 
          className="absolute top-3 left-3 z-30 px-2 py-1 rounded text-xs font-bold uppercase"
          style={{ 
            background: `${colors.main}40`,
            color: colors.main,
            border: `1px solid ${colors.main}`,
          }}
        >
          {card.rarity}
        </div>
        
        {/* Bottom info bar */}
        <div className="absolute bottom-0 left-0 right-0 z-30">
          {/* News Ticker */}
          {showNews && currentEvent && (
            <AnimatePresence>
              <motion.div
                className="px-3 py-1.5 flex items-center gap-2 overflow-hidden"
                style={{ 
                  background: isBuffed ? 'rgba(0,255,0,0.2)' : 'rgba(255,0,0,0.2)',
                  borderTop: `1px solid ${isBuffed ? '#00FF00' : '#FF0000'}`,
                }}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 20, opacity: 0 }}
              >
                <Zap className={`w-3 h-3 flex-shrink-0 ${isBuffed ? 'text-green-400' : 'text-red-400'}`} />
                <motion.p
                  className="font-space text-[10px] text-white whitespace-nowrap"
                  animate={{ x: ['0%', '-100%'] }}
                  transition={{ duration: 10, repeat: Infinity, ease: 'linear' }}
                >
                  {currentEvent.headline} • {currentEvent.headline}
                </motion.p>
              </motion.div>
            </AnimatePresence>
          )}
          
          {/* Name and title */}
          <div className="p-3 bg-gradient-to-t from-black via-black/90 to-transparent">
            <h3
              className="font-anton text-lg uppercase tracking-tight text-center"
              style={{ color: colors.main, textShadow: `0 0 10px ${colors.glow}` }}
              data-testid={`card-name-${card.id}`}
            >
              {card.character_name}
            </h3>
            <p className="font-bebas text-xs uppercase tracking-widest text-center text-white/60">
              {card.title}
            </p>
          </div>
        </div>
        
        {/* Duplicate badge */}
        {card.is_duplicate && (
          <div className="absolute top-12 left-3 z-30 px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-yellow-500/80 text-black">
            DUPE
          </div>
        )}
        
        {/* Corner decorations - cyberpunk style */}
        <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 z-40" style={{ borderColor: colors.main }} />
        <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 z-40" style={{ borderColor: colors.main }} />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 z-40" style={{ borderColor: colors.main }} />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 z-40" style={{ borderColor: colors.main }} />
      </motion.div>
    </Tilt>
  );
};

export default EnhancedCard;
